/**
 * Mirror API — onboarding a manuscript channel onto the two-hop backup mirror.
 *
 * Backend: `app/api/mirror.py`. Every route is admin-gated (the manuscript
 * tier, `manuscript_role='admin'`), so a reviewer sees mirror status through
 * these calls only if the page asks for them — it does not.
 *
 * Telegram peer ids are STRINGS on the wire (`bigint` in the DB); only
 * `source_chats_id` / `account_id` / `backup_channel_id` are real numbers.
 */

import apiClient from './client';

/** The five-gate verdict, ported from the ilm dashboard's /mirror page. */
export type MirrorStatus = 'ready' | 'paused' | 'broken';

export interface MirrorPosterAccount {
  id: number;
  label: string;
  status: string;
  role: string;
  last_connected_at: string | null;
}

/** A proven membership: only rows with a non-NULL `verified_at` are returned. */
export interface MirrorMembership {
  chats_id: number;
  account_id: number;
  verified_at: string;
}

/**
 * The two master switches, tri-state on purpose: `null` = the row is missing or
 * could not be read. Both `null` and `false` mean "not satisfied" — never
 * render an unknown switch as if it were on.
 */
export interface MirrorPreconditions {
  archive_chat_id: string;
  mirror_send_enabled: boolean | null;
  mirror_enabled: boolean | null;
}

export interface MirrorOnboarding {
  accounts: MirrorPosterAccount[];
  memberships: MirrorMembership[];
  preconditions: MirrorPreconditions;
  suggested_backup_channel: string | null;
}

export interface MirrorChannel {
  backup_channel_id: number;
  title: string;
  source_chats_id: number | null;
  source_name: string | null;
  source_chat_id: string | null;
  backup_channel: string | null;
  bc_status: string;
  status: MirrorStatus;
  /** Arabic reasons, empty when ready. Same order as the dashboard's. */
  why_not: string[];
  has_archive_route: boolean;
  archive_chat_id: string | null;
  archive_enabled: boolean;
  /** NULL until the bot lazily creates the forum topic on first delivery. */
  archive_topic_id: number | null;
  poster_account_id: number | null;
  poster_label: string | null;
  poster_enabled: boolean;
  poster_verified_at: string | null;
  forwarded_count: number;
  added_at: string | null;
}

export interface MirrorCreateRequest {
  source_chats_id: number;
  backup_channel: string;
  account_id: number;
  title?: string;
  /** The operator's confirmation of the archive/forum precondition. */
  archive_precondition_ack: boolean;
}

export interface MirrorCreateResult {
  ok: boolean;
  backup_channel_id: number;
  source_chats_id: number;
  backup_channel: string;
  archive_chat_id: string;
  account_id: number;
}

export interface MirrorToggleResult {
  ok: boolean;
  backup_channel_id: number;
  bc_status: string;
}

export const getMirrorOnboarding = async (): Promise<MirrorOnboarding> => {
  const res = await apiClient.get<MirrorOnboarding>('/mirror/onboarding');
  return res.data;
};

export const listMirrorChannels = async (): Promise<MirrorChannel[]> => {
  const res = await apiClient.get<MirrorChannel[]>('/mirror/channels');
  return res.data;
};

export const createMirrorChannel = async (
  body: MirrorCreateRequest
): Promise<MirrorCreateResult> => {
  const res = await apiClient.post<MirrorCreateResult>('/mirror/channels', body);
  return res.data;
};

/** Pause (enabled=false) or resume (enabled=true). There is no delete. */
export const toggleMirrorChannel = async (
  backupChannelId: number,
  enabled: boolean,
  accountId?: number
): Promise<MirrorToggleResult> => {
  const res = await apiClient.post<MirrorToggleResult>(
    `/mirror/channels/${backupChannelId}/toggle`,
    { enabled, ...(accountId != null ? { account_id: accountId } : {}) }
  );
  return res.data;
};
